import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import WarningAlertIcon from "../../../../../assets/images/icons/WarningAlertIcon";
import { SkeletonImage } from "../../../../../components/common/Skeleton/SkeletonGallery";
import { PlaygroundImage } from "../../../../../models/image-to-image";
import styles from "./ThumbnailSelector.module.scss";

export type ThumbnailSelectorProps = {
	images: PlaygroundImage[];
	selectedIndex: number;
	onSelect: (index: number) => void;
};

type ThumbnailItemProps = {
	image: PlaygroundImage;
	index: number;
	isSelected: boolean;
	onSelect: (index: number) => void;
};

const ThumbnailItem = observer(({ image, index, isSelected, onSelect }: ThumbnailItemProps) => {
	const hasError = !!image.error;
	const isLoading = image.loading;

	return (
		<Box
			key={image.id || index}
			className={clsx(styles.thumbnail, {
				[styles.selected]: isSelected,
				[styles.error]: hasError,
				[styles.loading]: isLoading,
			})}
			onClick={() => onSelect(index)}
		>
			{isLoading ? (
				<SkeletonImage className={styles.skeleton} aspectRatio="16:9" />
			) : hasError ? (
				<Box className={styles.errorIcon}>
					<WarningAlertIcon width={20} height={20} />
				</Box>
			) : (
				<LazyLoadImage
					src={image.url}
					alt={`Thumbnail ${index + 1}`}
					effect="blur"
					className={styles.thumbnailImage}
				/>
			)}
		</Box>
	);
});

const ThumbnailSelector = ({ images, selectedIndex, onSelect }: ThumbnailSelectorProps) => {
	if (images.length <= 1) return null;

	return (
		<Box className={styles.thumbnailSelector}>
			{images.map((image, index) => (
				<ThumbnailItem
					key={image.id || index}
					image={image}
					index={index}
					isSelected={index === selectedIndex}
					onSelect={onSelect}
				/>
			))}
		</Box>
	);
};

export default observer(ThumbnailSelector);
